import { Button, DatePicker, Form, Input, Select } from "antd";
import type { UploadFile } from "antd";
import apiFactory from "api";
import { ButtonSolid } from "component/button";
import { DATE_FORMAT_6, JP_DATE_TIME_FORMAT } from "constant/date";
import MESSAGES from "constant/messages";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  IncidentReportFields,
  NearMissFields,
  NotificationCategory,
  notificationCategoryOptions,
  RestFields,
} from "utils/notification";
import { INotification } from "constant/interfaces";
import dayjs from "dayjs";
import FormItemFile from "page/admin/Notice/components/form/FormItemFile";
import ROUTES from "constant/routes";
import { isString } from "lodash";
import Seen from "page/admin/Notice/components/form/Seen";

interface IProps {
  notificationDetail?: INotification;
  isEdit?: boolean
}
const CoveyedForm = ({ notificationDetail, isEdit }: IProps) => {
  const navigate = useNavigate();
  const [form] = Form.useForm<any>();
  const notificationCategoryId = Form.useWatch(
    "notification_category_id",
    form
  );
  // In edit mode, form starts in view-only state until user clicks "編集" buttonnơm n
  const [disabled, setDisabled] = useState(isEdit)
  const [submitting, setSubmitting] = useState(false);

  const handleSubmitData = async (data: any) => {
    try {
      setSubmitting(true);

      const formData = new FormData();
      Object.keys(data).forEach((key) => {
        if (key === "file" && typeof data.file !== "string") {
          const fileList = data.file;
          const file = Array.isArray(fileList) ? fileList[0] : undefined;
          if (file?.originFileObj) {
            formData.append("file", file.originFileObj);
          }
        } else {
          const value = data[key];
          if (value != null && value !== "") {
            formData.append(
              key,
              typeof value === "object" ? JSON.stringify(value) : String(value)
            );
          }
        }
      });
      if (notificationDetail?.file_url && !data.file?.length) {
        formData.append("is_delete_attached", "true")
      }
      const result =
        isEdit && notificationDetail ? await apiFactory.staffNoticeApi.updateNotification(formData, notificationDetail.id) : await  apiFactory.staffNoticeApi.createNoticeGroup(formData);

      if (result) {
        isString(result.success) && toast.success(result.success)
        navigate("/staff/" + ROUTES.BULLETINBOARD);
        
      }
    } catch (error) {
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };
  const fields = useMemo(() => {
    if (notificationCategoryId === NotificationCategory.NearMiss) {
      return NearMissFields;
    }
    if (notificationCategoryId === NotificationCategory.IncidentReport) {
      return IncidentReportFields;
    }
    return RestFields;
  }, [notificationCategoryId]);

  const renderFields = () => {
    return fields.map((field) => {
      if (!field) return null;
      const label = field.label; 

      if (field.type === "file") {
        return (
          <FormItemFile
            name={field.name}
            label={label}
            required={field.required}
            disabled={disabled}
          />
        );
      }
      if (field.type === "poster") {
        return (
          notificationDetail && <Form.Item label={label} required={field.required}>
            <Input
              placeholder={label}
              disabled={!!field.disabled}
              value={notificationDetail?.poster}
            />
          </Form.Item>
        );
      }

      if (field.type === "seen") {
        return (
          notificationDetail && <Form.Item label={label} required={field.required}>
            <Seen item={notificationDetail}/>
          </Form.Item>
        );
      }

      if (field.type === "dateTime") {
        return (
          <Form.Item
            label={label}
            rules={[{ required: field.required, message: MESSAGES.requireField }]}
            name={field.name}
            getValueProps={(value) => ({
              value: value ? dayjs(value, JP_DATE_TIME_FORMAT) : null,
            })}
            normalize={(value) =>
              value ? value.format(DATE_FORMAT_6) : null
            }
          >
            <DatePicker
              format={JP_DATE_TIME_FORMAT}
              showTime
              placeholder={label}
            />
          </Form.Item>
        );
      }

      return (
        <Form.Item
          key={field.name}
          label={label}
          name={field.name}
          rules={[{ required: field.required, message: MESSAGES.requireField }]}
        >
          {field.type === "text" && <Input placeholder={label} />}
          {field.type === "textarea" && <Input.TextArea placeholder={label} />}

          {field.type === "select" && (
            <Select
              options={(field as any).options || []}
              placeholder={label}
            />
          )}
        </Form.Item>
      );
    });
  };

  const convertData = (notificationDetail: INotification) => {
    let fileList: UploadFile[] = [];

    if (notificationDetail.file_url?.image_path) {
      const url = notificationDetail.file_url.image_path;
      const nameFromPath = url.split("/").pop() || "file";

      fileList = [
        {
          uid: notificationDetail.file_url.signed_id,
          name: decodeURIComponent(nameFromPath),
          status: "done",
          url,
        } as UploadFile,
      ];
    }

    return {
      ...notificationDetail,
      file: fileList,
      notification_category_id: notificationDetail.notification_category?.id,
    };
  };

  useEffect(() => {
    if (notificationDetail) {
      form.setFieldsValue(convertData(notificationDetail));
    }
  }, [notificationDetail, form]);


  return (
    <>
      <Form form={form} onFinish={handleSubmitData} disabled={disabled}>
        <Form.Item
          label="カテゴリー"
          name="notification_category_id"
          rules={[{ required: true, message: MESSAGES.requireField }]}
        >
          <Select
            options={notificationCategoryOptions}
            allowClear
            placeholder="選択してください"
          />
        </Form.Item>

        {renderFields()}
      </Form>

      <div className="flex items-center justify-end">
        <Button
          className="!h-[35px] !px-[16px] !text-[14px] !leading-[24px] w-[160px]"
          onClick={() =>   navigate("/staff/" + ROUTES.BULLETINBOARD)}
        >
          キャンセル
        </Button>

        {
          disabled ? <ButtonSolid
            className="!h-[35px] !px-[54px] !text-[14px] !leading-[24px] w-[160px] ml-3"
            onClick={(e) => {
              e.preventDefault();
              setDisabled(false)
            }}
          >
            編集
          </ButtonSolid> : <ButtonSolid
           
            className="!h-[35px] !px-[54px] !text-[14px] !leading-[24px] w-[160px] ml-3"
            isLoading={submitting}
            onClick={form.submit}
          >
            保存
          </ButtonSolid>
        }
      </div></>
  );
};
export default CoveyedForm;
